import { Link, useLocation } from "react-router";

import { PageLayout, RobotoDivider } from "@/shared/components";
import {
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components/RobotoStyledTabs";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Goto } from "@/shared/navigation";

import styles from "./Settings.module.css";
import { SettingsTabRoutes } from "./SettingsTabRoutes";
import {
  useActiveSettingsTab,
  SettingsTab,
} from "./useActiveSettingsTabFromUrl";

interface SettingsTabLink {
  tab: SettingsTab;
  label: string;
  pathname: string;
  /** Shown only while this is true; a tab without it is always shown. */
  shown?: boolean;
}

export function Settings() {
  const location = useLocation();

  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");
  const sessionIngestionEnabled = useCheckFeatureFlag(
    "release-session-ingestion",
  );

  const activeTab = useActiveSettingsTab();

  const tabs: SettingsTabLink[] = [
    {
      tab: SettingsTab.Organization,
      label: "Organization",
      pathname: Goto.route.Settings.Organization,
    },
    {
      tab: SettingsTab.Tokens,
      label: "Tokens",
      pathname: Goto.route.Settings.Tokens,
    },
    {
      tab: SettingsTab.Usage,
      label: "Usage",
      pathname: Goto.route.Settings.Usage,
    },
    { tab: SettingsTab.AI, label: "AI", pathname: Goto.route.Settings.AI },
    {
      tab: SettingsTab.Secrets,
      label: "Secrets",
      pathname: Goto.route.Settings.Secrets,
    },
    {
      tab: SettingsTab.Ingestion,
      label: "Ingestion",
      pathname: Goto.route.Settings.Ingestion.Root,
    },
    {
      tab: SettingsTab.Sessions,
      label: "Sessions",
      pathname: Goto.route.Settings.Sessions,
      shown: sessionIngestionEnabled,
    },
    {
      tab: SettingsTab.Integrations,
      label: "Integrations",
      pathname: Goto.route.Settings.Integrations.Root,
    },
    {
      tab: SettingsTab.CustomFields,
      label: "Custom Fields",
      pathname: Goto.route.Settings.CustomFields,
      shown: customFieldsEnabled,
    },
  ];
  const shownTabs = tabs.filter(({ shown }) => shown !== false);

  // A gated tab isn't rendered while its gate is off, including the brief
  // window where a flag is still loading and reads false. Passing that tab's
  // value to <Tabs> would trip MUI's "no matching child" warning, so its URL
  // selects no tab until it appears.
  const selectedTab = shownTabs.some(({ tab }) => tab === activeTab)
    ? activeTab
    : false;

  return (
    <PageLayout title={"Roboto - Settings"} headerContentLeft={"Settings"}>
      <div>
        <RobotoStyledTabs value={selectedTab} aria-label="settings tabs">
          {shownTabs.map(({ tab, label, pathname }) => (
            <RobotoStyledTab
              key={tab}
              value={tab}
              data-cy={tab}
              label={label}
              component={Link}
              to={{ pathname, search: location.search }}
              replace
            />
          ))}
        </RobotoStyledTabs>

        <RobotoDivider />
      </div>

      <div className={styles.body}>
        <SettingsTabRoutes />
      </div>
    </PageLayout>
  );
}
